import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faSpinner, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { generateClient } from 'aws-amplify/api';
import { getProtocols } from '@/graphql/queries';
import moment from 'moment';

const purple = "#2E2868";
const client = generateClient();

const ProtocolRow = ({ protocol, onView }) => (
    <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom" style={{ fontSize: "14px" }}>
        <div className="d-flex align-items-start">
            <div className="me-3 mt-1 text-muted">
                <FontAwesomeIcon icon={faFilePdf} size="lg" />
            </div>
            <div>
                <div className="fw-bold mb-1" style={{ color: "#333" }}>{protocol.name}</div>
                <div style={{ fontSize: "12px", color: "#777" }}>
                    Uploaded: {protocol.uploaded} | {protocol.size} | Version {protocol.version}
                </div>
            </div>
        </div>

        <div
            onClick={() => onView(protocol.url)}
            className="d-flex align-items-center justify-content-center shadow-sm"
           
            title={protocol.url ? "View Protocol" : "URL not available"}
            style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                backgroundColor: protocol.url ? purple : "#ccc", 
                color: "#fff",
                cursor: protocol.url ? "pointer" : "not-allowed",
                minWidth: "32px" 
            }}
        >
            <FontAwesomeIcon icon={faEye} size="sm" />
        </div>
    </div>
);

const ProtocolSection = ({ subject }) => {
  const [protocols, setProtocols] = useState({ all: [], current: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchProtocols = async () => {
      try {
        setLoading(true);

        // 1. Fetch ALL Protocols
        const allPromise = client.graphql({
            query: getProtocols,
            variables: { limit: 100 }
        });

        // 2. Fetch Assigned Protocols
        const currentPromise = subject?.id 
            ? client.graphql({
                query: getProtocols,
                variables: { subject_id: [subject.id] } 
              })
            : Promise.resolve({ data: { getProtocols: { protocols: [] } } });

        const [allResult, currentResult] = await Promise.all([allPromise, currentPromise]);

        // --- Helper Function Updated for New Schema ---
        const processList = (list) => {
            return (list || []).map(p => {
                let meta = {};
                
                // Parse JSON safely if it exists (for filename/size/metadata)
                if (p.protocol_extract_json) {
                    try {
                        meta = JSON.parse(p.protocol_extract_json);
                    } catch (e) {
                        console.warn(`JSON Warning for protocol ${p.id}`, e);
                        // Continue execution even if JSON fails
                    }
                }

                
                const finalUrl = p.protocol_url || meta.url;

                return {
                    id: p.id,
                   
                    name: p.protocol_name || meta.title || "Untitled Protocol",
                    uploaded: p.created_at ? moment(p.created_at).format("MMM D, YYYY") : "N/A",
                    size: meta.size || "N/A",
                    version: "1.0",
                    url: finalUrl 
                };
            }).filter(Boolean);
        };

        if (isMounted) {
            setProtocols({
                all: processList(allResult?.data?.getProtocols?.protocols),
                current: processList(currentResult?.data?.getProtocols?.protocols)
            });
        }

      } catch (err) {
        console.error("🔥 Error loading protocols:", err);
        if (isMounted) setError("Failed to load protocols library.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProtocols();

    return () => { isMounted = false; };
  }, [subject]); 

  const handleViewProtocol = (url) => {
    if (!url) {
        
        alert("Document URL not found. Please contact support.");
        return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="container-fluid mt-3 px-0 w-100">
      <div className="p-3">
        
        {loading && (
            <div className="text-center py-4">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" color={purple} />
                <div className="mt-2 text-muted small">Loading protocols library...</div>
            </div>
        )}
        
        {error && <div className="text-danger small mb-3">{error}</div>}

        {!loading && (
            <>
                {/* 1. Assigned / Current Protocols */}
                <div className="mb-5">
                    <div className="d-flex align-items-center mb-3">
                        <h6 className="fw-bold m-0 text-uppercase" style={{ color: purple, letterSpacing: '0.5px' }}>
                            Current Study Protocol
                        </h6>
                        {protocols.current.length > 0 && (
                             <span className="badge bg-success ms-2" style={{fontSize: '10px'}}>Active Match</span>
                        )}
                    </div>
                    <div>
                        {protocols.current.length > 0 ? (
                            protocols.current.map((p, i) => (
                                <ProtocolRow key={`curr-${i}`} protocol={p} onView={handleViewProtocol} />
                            ))
                        ) : (
                            <div className="text-muted small py-2 fst-italic">
                                No specific protocols assigned to this subject.
                            </div>
                        )}
                    </div>
                </div>

                {/* 2. All Library Protocols */}
                <div>
                    <div className="d-flex align-items-center mb-3">
                         <h6 className="fw-bold m-0 text-uppercase" style={{ color: purple, letterSpacing: '0.5px' }}>
                            All Available Protocols
                        </h6>
                    </div>
                    
                    <div>
                        {protocols.all.length > 0 ? (
                            protocols.all.map((p, i) => (
                                <ProtocolRow key={`all-${i}`} protocol={p} onView={handleViewProtocol} />
                            ))
                        ) : (
                            !error && (
                                <div className="text-muted small py-2 fst-italic">
                                    No protocols found in the database.
                                </div>
                            )
                        )}
                    </div>
                </div>
            </>
        )}
      </div>
    </div>
  );
};

export default ProtocolSection;